import { Component } from '@theme/component';
import { SubstanceAnswer } from './search-substance-answer.js';

/**
 * Search results page (sections/main-search.liquid).
 *
 * - Fills the substance answer slot (same module as the predictive search modal).
 * - Loads articles + pages into `[data-main-search-content-slot]` via the Section
 *   Rendering API with `type=article,page`: Shopify only returns a handful of
 *   mixed results while products are among the searched types.
 * - "Mehr laden" appends the next page of content results.
 * - Hides the no-results fallback again when content turns up.
 *
 * Analytics (window.phCapture; result clicks are covered by PostHog autocapture):
 *   search_page_viewed       { query, products, content_results, substance }
 *   search_page_more_loaded  { query, page }
 *
 * @extends Component
 */
class MainSearchComponent extends Component {
  async connectedCallback() {
    super.connectedCallback();

    const { query = '', products = '0' } = this.dataset;
    if (!query) return;

    const answer = SubstanceAnswer.fromElement(this);
    const [substance, contentResults] = await Promise.all([
      answer ? answer.render(query, this, () => !this.isConnected) : null,
      this.#loadContent(query),
    ]);

    window.phCapture?.('search_page_viewed', {
      query,
      products: Number(products),
      content_results: contentResults,
      substance,
    });
  }

  /**
   * "Mehr laden" link (`on:click`); a plain link to the next page without JS.
   * @param {MouseEvent} event
   */
  async loadMore(event) {
    const link = event.target instanceof HTMLElement ? event.target.closest('a') : null;
    if (!link) return;
    event.preventDefault();
    link.setAttribute('aria-busy', 'true');

    const content = await this.#fetchContent(new URL(link.href, location.origin));
    const current = link.closest('[data-main-search-content], [data-main-search-content-slot]');
    if (!content || !current) {
      window.location.href = link.href;
      return;
    }

    for (const group of content.querySelectorAll('[data-main-search-group]')) {
      if (!(group instanceof HTMLElement)) continue;
      const existing = current.querySelector(`[data-main-search-group="${group.dataset.mainSearchGroup}"] [data-main-search-list]`);
      const items = group.querySelector('[data-main-search-list]');
      if (existing && items) existing.append(...items.children);
      else current.insertBefore(group, link);
    }

    const next = content.querySelector('[data-main-search-more]');
    if (next) link.replaceWith(next);
    else link.remove();

    window.phCapture?.('search_page_more_loaded', {
      query: this.dataset.query ?? '',
      page: Number(new URL(link.href, location.origin).searchParams.get('page') ?? 0),
    });
  }

  /**
   * @param {string} query
   * @returns {Promise<number>} Number of content results (articles + pages).
   */
  async #loadContent(query) {
    const slot = this.querySelector('[data-main-search-content-slot]');
    if (!(slot instanceof HTMLElement)) return Number(this.querySelector('[data-main-search-content]')?.getAttribute('data-main-search-content') ?? 0);

    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'article,page');

    const content = await this.#fetchContent(url);
    if (!content) return 0;

    slot.replaceChildren(...content.children);
    const count = Number(content.dataset.mainSearchContent ?? 0);

    // Something matched after all: the "no results" fallback no longer applies.
    if (count > 0) this.querySelector('[data-main-search-fallback]')?.setAttribute('hidden', '');
    return count;
  }

  /**
   * Fetch this section rendered for a content-only search and return its content wrapper.
   * @param {URL} url
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchContent(url) {
    const { sectionId } = this.dataset;
    if (!sectionId) return null;
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-main-search-content]');
      return content instanceof HTMLElement ? content : null;
    } catch {
      return null;
    }
  }
}

if (!customElements.get('main-search-component')) {
  customElements.define('main-search-component', MainSearchComponent);
}
